import type { WidgetSDK as IWidgetSDK, WidgetSDKConfig } from './widget-sdk.types'
import type { EventHandler } from './event-emitter.types'
import type { DesignTokens } from './design-token-store'
import type { VersionInfo } from '../version'
import type { WidgetSignals } from '../signals'
import { WIDGET_SIGNAL_EVENT } from '../signals'
import { EventEmitter } from './event-emitter'
import { VERSION, getVersionInfo } from '../version'
import { getDesignTokens as getTokens, registerEmitter, unregisterEmitter } from './design-token-store'

export class WidgetSDK implements IWidgetSDK {
  readonly shadowRoot: ShadowRoot
  readonly signals: WidgetSignals
  private readonly widgetType: string
  private readonly instanceId?: string
  private props: Record<string, unknown>
  private readonly emitter: EventEmitter
  private ready: boolean = false
  private readonly readyPromise: Promise<IWidgetSDK>
  private readyResolve!: (sdk: IWidgetSDK) => void

  constructor(config: WidgetSDKConfig) {
    this.shadowRoot = config.shadowRoot
    this.widgetType = config.widgetType
    this.instanceId = config.instanceId
    this.props = { ...config.props }
    this.emitter = new EventEmitter()
    this.signals = this.createSignals()

    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve
    })

    registerEmitter(this.emitter)
  }

  getInstanceId(): string | undefined {
    return this.instanceId
  }

  // `sdk.signals` over the emitter; outbound reports also surface to the host (dispatchReport).
  private createSignals(): WidgetSignals {
    const signals: WidgetSignals = {
      on: (signal, handler) => this.emitter.on(signal.name, handler as EventHandler),
      report: (signal, ...payload) => {
        const data = payload[0]
        this.emitter.emit(signal.name, data)
        this.dispatchReport(signal.name, data)
      }
    }
    return signals
  }

  private dispatchReport(name: string, payload: unknown): void {
    const host = this.shadowRoot.host
    host?.dispatchEvent(
      new CustomEvent(WIDGET_SIGNAL_EVENT, {
        detail: { instanceId: this.instanceId, name, payload },
        bubbles: true,
        composed: true
      })
    )
  }

  /** @internal */
  _markReady(): void {
    if (!this.ready) {
      this.ready = true
      this.readyResolve(this)
    }
  }

  /** @internal */
  _destroy(): void {
    this.emitter.emit('destroy')
    unregisterEmitter(this.emitter)
    this.emitter.removeAllListeners()
  }

  whenReady(): Promise<IWidgetSDK> {
    return this.readyPromise
  }

  getProps<T extends Record<string, unknown> = Record<string, unknown>>(): T {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return { ...this.props } as T
  }

  setProps(newProps: Record<string, unknown>): void {
    this.props = { ...this.props, ...newProps }
    this.emitter.emit('propsChanged', this.props)
  }

  get root(): ShadowRoot {
    return this.shadowRoot
  }

  getContainer(): ShadowRoot {
    return this.shadowRoot
  }

  $<T extends Element = Element>(selector: string): T | null {
    return this.shadowRoot.querySelector<T>(selector)
  }

  $$<T extends Element = Element>(selector: string): T[] {
    return Array.from(this.shadowRoot.querySelectorAll<T>(selector))
  }

  // eslint-disable-next-line class-methods-use-this
  get document(): Document {
    return globalThis.document
  }

  // eslint-disable-next-line class-methods-use-this
  get version(): string {
    return VERSION
  }

  // eslint-disable-next-line class-methods-use-this
  getVersionInfo(): VersionInfo {
    return getVersionInfo()
  }

  on<T = unknown>(event: string, handler: EventHandler<T>): () => void {
    return this.emitter.on(event, handler)
  }

  off(event: string, handler: EventHandler): void {
    this.emitter.off(event, handler)
  }

  emit(event: string, data?: unknown): void {
    this.emitter.emit(event, data)
  }

  // eslint-disable-next-line class-methods-use-this
  getDesignTokens(): DesignTokens {
    return getTokens()
  }

  /** @internal */
  getWidgetType(): string {
    return this.widgetType
  }
}
